import { asBoolean, asStringArray } from '@/utils'
import { type UpdateFormFn, useForm } from '@ur-solutions/react-hooks'
import { createContext, useContext } from 'react'

export const CURRENT_YEAR = new Date().getFullYear()

export interface DriverWorkFilter {
  year: number
  drivers: string[]
  ownedVehiclesOnly: boolean
}

interface DriverWorkFilterForm {
  filter: DriverWorkFilter
  updateFilter: UpdateFormFn<DriverWorkFilter>
}

/**
 * The working time filters, kept in session storage under one name so the
 * views and tabs of the page share them.
 */
export const useDriverWorkFilterForm = (): DriverWorkFilterForm => {
  const { formValues, updateForm } = useForm<DriverWorkFilter>({
    values: { year: CURRENT_YEAR, drivers: [], ownedVehiclesOnly: false },
    config: {
      storage: {
        storeFormState: true,
        storeFormStateName: 'driver-work-filters',
        sessionStorage: true,
        retrieveFormStateOnMount: true,
        transformValues: {
          year: value =>
            typeof value === 'number' && value <= CURRENT_YEAR
              ? value
              : undefined,
          drivers: asStringArray,
          ownedVehiclesOnly: asBoolean,
        },
      },
    },
  })

  return { filter: formValues, updateFilter: updateForm }
}

export const DriverWorkFilterContext =
  createContext<DriverWorkFilterForm | null>(null)

/** The page's filter form, for components inside `DriverWorkLayout`. */
export const useDriverWorkFilter = (): DriverWorkFilterForm => {
  const form = useContext(DriverWorkFilterContext)
  if (!form)
    throw new Error('useDriverWorkFilter must be used inside DriverWorkLayout')
  return form
}

/** Keep the rows of the drivers chosen in the filter, or all without a choice. */
export const filterDrivers = <TRow extends { user: { id: string } }>(
  rows: TRow[],
  drivers: string[]
) => (drivers.length ? rows.filter(row => drivers.includes(row.user.id)) : rows)
